import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Card } from '@factorialco/f0-react'
import { TwoColumnsList } from '@factorialco/f0-react/dist/experimental'
import { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'

import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import { aggregateMinutes } from 'modules/attendance/components/TimeTrackingCollection/lib/aggregateMinutes'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'

interface OverviewTabProps {
  employee: AttendanceCustomPeriodEmployeeItemFragment
}

const HoursCalculationEmptyState = ({
  readyDate,
  isComplementary = false,
}: {
  readyDate?: string
  isComplementary?: boolean
}) => {
  const emptyStateMessage = isComplementary
    ? readyDate
      ? i18n.t(
          'time_tracking.table_collection.employee_summary.complementary_hours.ready_on_date',
          { date: readyDate }
        )
      : i18n.t('time_tracking.table_collection.employee_summary.complementary_hours.ready_soon')
    : readyDate
    ? i18n.t('time_tracking.table_collection.employee_summary.extra_hours.ready_on_date', {
        date: readyDate,
      })
    : i18n.t('time_tracking.table_collection.employee_summary.extra_hours.ready_soon')

  const title = isComplementary
    ? i18n.t(
        'time_tracking.table_collection.employee_summary.complementary_hours.calculation_in_progress'
      )
    : i18n.t('time_tracking.table_collection.employee_summary.extra_hours.calculation_in_progress')

  return (
    <Box textAlign="center">
      <F0Card title={title} description={emptyStateMessage} compact />
    </Box>
  )
}

export const OverviewTab = ({ employee }: OverviewTabProps) => {
  const policySettings =
    employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node

  const {
    plannedMinutes,
    balanceMinutes,
    dailyDays,
    effectiveWorkedMinutes,
    paidAbsencesMinutes,
    paidBreaksMinutes,
    workedMinutes,
    extraTime,
    complementaryTime,
    extraHoursCalculationOn,
    balancedRegularMinutes,
  } = aggregateMinutes({
    employee,
    extraHoursBaselineOverride: policySettings?.extraHoursBaseline,
    complementaryHoursBaselineOverride: policySettings?.complementaryHoursBaseline,
  })

  const workedMinutesFormatted = minutesToText(workedMinutes, true)
  const effectiveWorkedMinutesFormatted = minutesToText(effectiveWorkedMinutes, true)

  const isPartTime = Boolean(employee.referenceContract?.partTime)
  const extraTimeMinutes = isPartTime ? complementaryTime.minutes : extraTime.minutes
  const activeBaseline = isPartTime ? complementaryTime : extraTime
  const hasBaseline = activeBaseline.baseline !== undefined
  const baselineLabel =
    activeBaseline.baseline === 'contract_hours'
      ? i18n.t('time_tracking.table_collection.employee_summary.contract_hours')
      : i18n.t('time_tracking.table_collection.employee_summary.planned_hours')

  const comparedLabel =
    activeBaseline.baseline === 'contract_hours'
      ? i18n.t('time_tracking.table_collection.employee_summary.effective_worked_time')
      : i18n.t('time_tracking.table_collection.employee_summary.worked_time')

  const isDaily = Boolean(employee.referenceContract?.frForfaitJours)
  const readyDate = extraTimeMinutes === undefined ? extraHoursCalculationOn : undefined

  const { featureAvailability: hasComplementaryHours } = useAttendanceFeatures(
    'dev_time_tracking_complementary_hours'
  )
  const shouldShowComplementaryHours = hasComplementaryHours && isPartTime

  const extraHoursLabel = shouldShowComplementaryHours
    ? i18n.t('time_tracking.table_collection.employee_summary.complementary_hours.title')
    : i18n.t('time_tracking.table_collection.employee_summary.extra_hours.title')

  const extraHoursTooltipLabel = shouldShowComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.tooltips.complementary_hours.label')
    : i18n.t('time_tracking.timesheet.sidepanel.tooltips.extra_hours.label')

  const extraHoursTooltipDescription = shouldShowComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.tooltips.complementary_hours.description')
    : i18n.t('time_tracking.timesheet.sidepanel.tooltips.extra_hours.description')

  return (
    <>
      <TwoColumnsList
        title={i18n.t('time_tracking.table_collection.employee_summary.worked_time')}
        titleValue={
          isDaily
            ? i18n.t('time_tracking.day_abbreviation', {
                numberOfDays: dailyDays,
              })
            : workedMinutesFormatted
        }
        titleTooltip={{
          label: i18n.t(
            'time_tracking.table_collection.employee_summary.tooltips.worked_time.label'
          ),
          description: i18n.t(
            'time_tracking.table_collection.employee_summary.tooltips.worked_time.description'
          ),
        }}
        list={
          isDaily
            ? []
            : [
                {
                  title: i18n.t('time_tracking.table_collection.employee_summary.regular_hours'),
                  info: minutesToText(balancedRegularMinutes, true),
                },
                {
                  title: i18n.t('time_tracking.table_collection.employee_summary.paid_breaks'),
                  info: minutesToText(paidBreaksMinutes, true),
                },
                {
                  title: extraHoursLabel,
                  info: minutesToText(extraTimeMinutes, true),
                },
              ]
        }
      />
      {isDaily ? (
        <></>
      ) : (
        <>
          <TwoColumnsList
            title={i18n.t('time_tracking.table_collection.employee_summary.effective_worked_time')}
            titleValue={effectiveWorkedMinutesFormatted}
            titleTooltip={{
              label: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.label'
              ),
              description: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.description'
              ),
            }}
            list={[
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.worked_time'),
                info: workedMinutesFormatted,
              },
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.paid_absences'),
                info: minutesToText(paidAbsencesMinutes, true),
              },
            ]}
          />
          <TwoColumnsList
            title={i18n.t('time_tracking.table_collection.employee_summary.balance')}
            titleValue={minutesToText(balanceMinutes, true, true)}
            titleTooltip={{
              label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.label'),
              description: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.description'),
            }}
            list={[
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.planned_time'),
                info: minutesToText(plannedMinutes, true),
              },
              {
                title: i18n.t('time_tracking.table_collection.employee_summary.worked_time'),
                info: workedMinutesFormatted,
              },
            ]}
          />
          <TwoColumnsList
            title={extraHoursLabel}
            titleValue={minutesToText(extraTimeMinutes, true)}
            titleTooltip={{
              label: extraHoursTooltipLabel,
              description: extraHoursTooltipDescription,
            }}
            list={
              extraTimeMinutes === undefined
                ? []
                : [
                    {
                      title: baselineLabel,
                      info: hasBaseline ? minutesToText(activeBaseline.baselineMinutes, true) : '-',
                    },
                    {
                      title: comparedLabel,
                      info: hasBaseline ? minutesToText(activeBaseline.comparedMinutes, true) : '-',
                    },
                  ]
            }
          />
          {extraTimeMinutes === undefined && hasBaseline && (
            <HoursCalculationEmptyState
              readyDate={readyDate}
              isComplementary={shouldShowComplementaryHours}
            />
          )}
        </>
      )}
    </>
  )
}
